import { ActionButton, ActionIconButton, ActionToggleGroup } from '../../../components/ui/ActionButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { workoutDisplayDigits as displayDigits } from '../../../utils/workoutNumberFormat';
import { createPortal } from 'react-dom';
import { calculatePlannedMetricsFromBlocks } from '../../../utils/plannedWorkoutMetrics';
import { useTranslation } from 'react-i18next';
import {
  IoTimerOutline,
  IoFlashOutline,
  IoAddOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoCopyOutline,
  IoCreateOutline,
  IoOptionsOutline,
  IoReorderThreeOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import { useLongPressListReorder } from '../../../hooks/useLongPressListReorder';
import { moveItem } from './workoutBuilderReorder';
import type { ApiStructBlock, ApiStructStep } from '../../../utils/workoutStructure';
import {
  isOpenDurationStep,
  stepZoneColor,
  type StructureParseOptions,
} from '../../../utils/workoutStructure';
import {
  PALETTE_BLOCKS,
  type PaletteBlockDef,
} from '../../../utils/garminWorkoutConstants';
import {
  absoluteIndexFromStored,
  formatBuilderIntensityLabel,
  type PrimaryIntensityMetric,
} from '../../../utils/intensityMetricHelpers';
import { secToHMS } from '../../../utils/secToHMS';
import { WorkoutStructureBar } from '../../../components/workout/WorkoutStructureBar';
import { WorkoutBuilderSetupModal } from '../../../components/WorkoutBuilderSetupModal';
import {
  parseWorkoutBuilderSettings,
  type WorkoutBuilderSettings,
} from '../../../utils/workoutBuilderSettings';

export type WorkoutSettings = Pick<
  WorkoutBuilderSettings,
  'primaryLengthMetric' | 'primaryIntensityMetric' | 'primaryIntensityTargetOrRange'
>;

const DEFAULT_SETTINGS: WorkoutSettings = {
  primaryLengthMetric: 'duration',
  primaryIntensityMetric: 'percentOfFtp',
  primaryIntensityTargetOrRange: 'range',
};

const PILL_BAR_MAX_H = 32;

function describeStepDuration(step: ApiStructStep, lapLabel: string): string {
  if (isOpenDurationStep(step.openDuration)) return lapLabel;
  const unit = String(step?.length?.unit ?? '');
  const val = Number(step?.length?.value) || 0;
  if (unit.startsWith('second')) return secToHMS(val);
  if (unit === 'meter') return `${val} m`;
  return `${val}×`;
}

function buildPillBars(def: PaletteBlockDef): { bars: { val: number; color: string }[]; repeatCount?: number } {
  const block0 = def.blocks[0] as ApiStructBlock;
  const isRepeat = block0?.type === 'repetition';

  if (isRepeat) {
    const unit = (block0.steps ?? []).map(s => {
      const step = getInnerStep(s as ApiStructBlock);
      return {
        val: Number(step.targets?.[0]?.maxValue ?? 60),
        color: stepZoneColor(step),
      };
    });
    const repeatCount = Number(block0?.length?.value) || 1;
    return { bars: unit, repeatCount: repeatCount > 1 ? repeatCount : undefined };
  }

  const step = getInnerStep(block0);
  const val = Number(step.targets?.[0]?.maxValue ?? 60);
  const color = stepZoneColor(step);
  return { bars: [{ val, color }] };
}

function getInnerStep(item: ApiStructBlock): ApiStructStep {
  return ((item as ApiStructBlock & { steps?: ApiStructStep[] }).steps?.[0] ?? item) as ApiStructStep;
}

const ROOT_SCOPE = 'root';

function repeatScope(blockIdx: number): string {
  return `repeat-${blockIdx}`;
}

export interface WorkoutBuilderProps {
  data: ApiStructBlock[];
  onDragEnd: (repeatIndex: number | null, items: ApiStructBlock[]) => void;
  dragActive?: boolean;
  onDragStart?: () => void;
  Delete: (repeatIndex: number | null, item: ApiStructBlock, index: number) => void;
  Duplicate: (blockIndex: number) => void;
  selectedLoop?: string | number | null;
  loopPress?: (index: string | number | null) => void;
  onLoopPress: (index: string) => void;
  onItemPress: (index: number, parentIndex: number | null | undefined) => void;
  onSettingsChange: (settings: WorkoutSettings) => void;
  settingsConfirmed?: boolean;
  settingsToolbar?: HTMLElement | null;
  onSettingsConfirmed: () => void;
  /** First-run setup cancel — e.g. close fullscreen builder overlay. */
  onSetupCancel?: () => void;
  initialSettings?: WorkoutSettings | null;
  ftpValue?: number;
  ltHrValue?: number;
  maxHrValue?: number;
  thresholdSpeedMps?: number;
}

export function WorkoutBuilder({
  data,
  onDragEnd,
  dragActive,
  onDragStart,
  Delete,
  Duplicate,
  selectedLoop,
  loopPress,
  onLoopPress,
  onItemPress,
  onSettingsChange,
  settingsConfirmed = false,
  settingsToolbar,
  onSettingsConfirmed,
  onSetupCancel,
  initialSettings,
  ftpValue = 0,
  ltHrValue = 0,
  maxHrValue = 0,
  thresholdSpeedMps = 0,
}: WorkoutBuilderProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<WorkoutSettings>(parseWorkoutBuilderSettings(initialSettings ?? DEFAULT_SETTINGS));
  const [draftSettings, setDraftSettings] = useState<WorkoutSettings>(parseWorkoutBuilderSettings(initialSettings ?? DEFAULT_SETTINGS));
  const [setupModalOpen, setSetupModalOpen] = useState(!settingsConfirmed);
  const [undoSnapshot, setUndoSnapshot] = useState<ApiStructBlock[] | null>(null);
  const undoTimerRef = useRef<number | null>(null);

  const hasBlocks = Array.isArray(data) && data.length > 0;

  useEffect(() => {
    if (initialSettings) setSettings(parseWorkoutBuilderSettings(initialSettings));
  }, [initialSettings]);

  useEffect(() => {
    if (!settingsConfirmed) setSetupModalOpen(true);
  }, [settingsConfirmed]);

  useEffect(() => () => {
    if (undoTimerRef.current != null) window.clearTimeout(undoTimerRef.current);
  }, []);

  const rememberBeforeDelete = useCallback(() => {
    setUndoSnapshot(JSON.parse(JSON.stringify(data ?? [])) as ApiStructBlock[]);
    if (undoTimerRef.current != null) window.clearTimeout(undoTimerRef.current);
    undoTimerRef.current = window.setTimeout(() => setUndoSnapshot(null), 10000);
  }, [data]);

  const undoDelete = useCallback(() => {
    if (!undoSnapshot) return;
    onDragEnd(null, JSON.parse(JSON.stringify(undoSnapshot)) as ApiStructBlock[]);
    setUndoSnapshot(null);
    if (undoTimerRef.current != null) window.clearTimeout(undoTimerRef.current);
    undoTimerRef.current = null;
  }, [onDragEnd, undoSnapshot]);

  const appendBlocks = (blocks: ApiStructBlock[]) => {
    const cloned = JSON.parse(JSON.stringify(blocks)) as ApiStructBlock[];
    onDragEnd(null, [...(data ?? []), ...cloned]);
  };

  const totals = useMemo(() => {
    let secs = 0;
    let stepCount = 0;
    const addStep = (s: ApiStructStep) => {
      stepCount++;
      const unit = String(s?.length?.unit ?? '');
      secs += unit.startsWith('second') ? Number(s?.length?.value) || 0 : 0;
    };
    (data ?? []).forEach(block => {
      if (block.type === 'repetition') {
        const reps = Number(block?.length?.value) || 1;
        (block.steps ?? []).forEach(s => {
          for (let i = 0; i < reps; i++) addStep(getInnerStep(s as ApiStructBlock));
        });
      } else {
        addStep(getInnerStep(block));
      }
    });
    const metrics = calculatePlannedMetricsFromBlocks(data, {
      primaryIntensityMetric: settings.primaryIntensityMetric,
      primaryIntensityTargetOrRange: settings.primaryIntensityTargetOrRange,
      ftp: ftpValue, ltHr: ltHrValue, maxHr: maxHrValue, thresholdSpeedMps,
    });
    const seconds = metrics.total_time_planned || secs;
    return { durStr: seconds > 0 ? secToHMS(seconds) : '—', stepCount, tss: metrics.tss_planned };
  }, [data, settings.primaryIntensityMetric, settings.primaryIntensityTargetOrRange, ftpValue, ltHrValue, maxHrValue, thresholdSpeedMps]);

  const intensityThresholds = {
    ftp: ftpValue,
    ltHr: ltHrValue,
    maxHr: maxHrValue,
    thresholdSpeedMps,
  };

  const confirmSettings = () => {
    const rangeSettings = parseWorkoutBuilderSettings(draftSettings);
    setSettings(rangeSettings);
    onSettingsChange(rangeSettings);
    onSettingsConfirmed();
    setSetupModalOpen(false);
  };

  const openSettings = () => {
    setDraftSettings(settings);
    setSetupModalOpen(true);
  };

  const handleSetupCancel = () => {
    if (settingsConfirmed) {
      setDraftSettings(settings);
      setSetupModalOpen(false);
      return;
    }
    onSetupCancel?.();
  };

  const handleListReorder = useCallback(
    (scope: string, from: number, to: number) => {
      if (scope === ROOT_SCOPE) {
        onDragEnd(null, moveItem(data, from, to));
        return;
      }
      const match = /^repeat-(\d+)$/.exec(scope);
      if (!match) return;
      const blockIdx = Number(match[1]);
      const block = data[blockIdx];
      if (!block?.steps) return;
      onDragEnd(blockIdx, moveItem(block.steps as ApiStructBlock[], from, to));
    },
    [data, onDragEnd],
  );

  const moveInList = useCallback(
    (scope: string, index: number, direction: -1 | 1, siblingCount: number) => {
      const target = index + direction;
      if (target < 0 || target >= siblingCount) return;
      handleListReorder(scope, index, target);
    },
    [handleListReorder],
  );

  const renderMoveButtons = (
    scope: string,
    idx: number,
    siblingCount: number,
    variant: 'card' | 'repeat' = 'card',
  ) => {
    if (dragActive || siblingCount < 2) return null;
    const btnClass =
      variant === 'repeat'
        ? 'wb-mobile-repeat__btn wb-mobile-repeat__btn--move'
        : 'wb-mobile-step-card__action wb-mobile-step-card__action--move';
    return (
      <>
        <ActionIconButton
          type="button"
          className={btnClass}
          disabled={idx === 0}
          onClick={() => moveInList(scope, idx, -1, siblingCount)}
          title={t('workoutBuilder.moveUp')}
          label={t('workoutBuilder.moveUp')}
          icon={<IoChevronUpOutline aria-hidden />}
        />
        <ActionIconButton
          type="button"
          className={btnClass}
          disabled={idx === siblingCount - 1}
          onClick={() => moveInList(scope, idx, 1, siblingCount)}
          title={t('workoutBuilder.moveDown')}
          label={t('workoutBuilder.moveDown')}
          icon={<IoChevronDownOutline aria-hidden />}
        />
      </>
    );
  };

  const renderDragHandle = (scope: string, idx: number) => (
    <ActionIconButton
      type="button"
      className="wb-mobile-step-card__drag-hint"
      title={t('workoutBuilder.reorderHint')}
      onClick={e => e.stopPropagation()}
      onPointerDown={e => onSlotPointerDown(scope, idx, e)}
      label={t('workoutBuilder.reorderHint')}
      icon={<IoReorderThreeOutline aria-hidden />}
    />
  );

  const {
    onSlotPointerDown,
    slotProps,
    isDragging,
    isDropTarget,
    getSlotShift,
    isReordering,
    consumeSuppressedClick,
  } = useLongPressListReorder({
    onReorder: handleListReorder,
    onDragStart,
    onDragEnd: () => onDragEnd(null, data),
  });

  const renderStepCard = (
    step: ApiStructStep,
    idx: number,
    parentIdx: number | null,
    onDelete: () => void,
    nested?: boolean,
    onDuplicate?: () => void,
    siblingCount = 1,
  ) => {
    const scope = parentIdx != null ? repeatScope(parentIdx) : ROOT_SCOPE;
    const dragging = isDragging(scope, idx);
    const dropTarget = isDropTarget(scope, idx);
    const shift = getSlotShift(scope, idx);
    const parseOptions: StructureParseOptions = {
      ftp: intensityThresholds.ftp,
      ltHr: intensityThresholds.ltHr,
      primaryIntensityMetric: settings.primaryIntensityMetric,
    };
    const accentColor = stepZoneColor(step, parseOptions);
    const durLabel = describeStepDuration(step, t('workoutBuilder.durationModeLap'));
    const tgt = step.targets?.[0];
    const powerLabel = tgt
      ? formatBuilderIntensityLabel(
          tgt.minValue,
          tgt.maxValue,
          settings.primaryIntensityMetric as PrimaryIntensityMetric,
          intensityThresholds,
          settings.primaryIntensityTargetOrRange,
        )
      : null;
    const openStep = () => {
      if (consumeSuppressedClick()) return;
      if (nested && parentIdx != null) onItemPress(idx, parentIdx);
      else onItemPress(parentIdx ?? idx, null);
    };

    return (
      <div
        key={`${parentIdx ?? 't'}-${idx}`}
        {...slotProps(scope, idx)}
        className={[
          'wb-mobile-step-card',
          nested ? 'wb-mobile-step-card--nested' : '',
          dragging ? 'wb-mobile-step-card--dragging' : '',
          dropTarget ? 'wb-mobile-step-card--drop-target' : '',
          shift === 'up' ? 'wb-mobile-step-card--shift-up' : '',
          shift === 'down' ? 'wb-mobile-step-card--shift-down' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="wb-mobile-step-card__accent" style={{ backgroundColor: accentColor }} />
        <div className="wb-mobile-step-card__body">
          <div className="wb-mobile-step-card__top">
            <button
              type="button"
              className="wb-mobile-step-card__title"
              onClick={openStep}
              aria-label={`${t('workoutBuilder.editStep')}: ${step.name}`}
            >
              <span className="wb-mobile-step-card__name">{step.name}</span>
            </button>
            {!dragActive && (
              <details className="wb-simple-actions" onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); } }}>
                <summary aria-label={t('workoutDetail.actions.more')}><span aria-hidden>•••</span></summary>
                <div className="wb-simple-actions__menu" onClick={e => { const button = (e.target as HTMLElement).closest('button'); if (button && !button.disabled) { const menu = e.currentTarget.closest('details'); if (menu) menu.open = false; } }}>
                <ActionIconButton
                  type="button"
                  className="wb-mobile-step-card__action wb-mobile-step-card__action--edit"
                  onClick={openStep}
                  title={t('workoutBuilder.editStep')}
                  label={t('workoutBuilder.editStep')}
                  icon={<IoCreateOutline aria-hidden />}
                />
                {renderDragHandle(scope, idx)}
                {renderMoveButtons(scope, idx, siblingCount)}
                {onDuplicate && (
                  <ActionIconButton
                    type="button"
                    className="wb-mobile-step-card__action wb-mobile-step-card__action--dup"
                    onClick={onDuplicate}
                    title={t('workoutBuilder.duplicateSection')}
                    label={t('workoutBuilder.duplicateSection')}
                    icon={<IoCopyOutline aria-hidden />}
                  />
                )}
                <ActionIconButton
                  type="button"
                  className="wb-mobile-step-card__action wb-mobile-step-card__action--del"
                  onClick={() => { rememberBeforeDelete(); onDelete(); }}
                  variant="danger"
                  label={t('workoutBuilder.deleteStep')}
                  icon={<IoTrashOutline aria-hidden />}
                />
              </div>
              </details>
            )}
          </div>
          <button
            type="button"
            className="wb-mobile-step-card__meta"
            onClick={openStep}
            tabIndex={-1}
            aria-hidden="true"
          >
            <span className="wb-mobile-meta-chip"><IoTimerOutline aria-hidden="true" />{durLabel}</span>
            {powerLabel && <span className="wb-mobile-meta-chip"><IoFlashOutline aria-hidden="true" />{powerLabel}</span>}
          </button>
        </div>
      </div>
    );
  };

  const renderRepeatBlock = (item: ApiStructBlock, blockIdx: number) => {
    const repeatCount = Number(item?.length?.value) || 1;
    const isSelected = selectedLoop === String(blockIdx);
    const innerSteps = item.steps ?? [];
    const scope = ROOT_SCOPE;
    const dragging = isDragging(scope, blockIdx);
    const dropTarget = isDropTarget(scope, blockIdx);
    const shift = getSlotShift(scope, blockIdx);

    return (
      <div
        key={`repeat-${blockIdx}`}
        {...slotProps(scope, blockIdx)}
        className={[
          'wb-mobile-repeat',
          isSelected ? 'wb-mobile-repeat--selected' : '',
          dragging ? 'wb-mobile-repeat--dragging' : '',
          dropTarget ? 'wb-mobile-repeat--drop-target' : '',
          shift === 'up' ? 'wb-mobile-repeat--shift-up' : '',
          shift === 'down' ? 'wb-mobile-repeat--shift-down' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => {
          if (consumeSuppressedClick()) return;
          loopPress?.(blockIdx);
        }}>
        <div className="wb-mobile-repeat__header">
          <div className="wb-mobile-repeat__left">
            <span className="wb-mobile-repeat__badge">{repeatCount}×</span>
            <span className="wb-mobile-repeat__label">{t('workoutBuilder.repeat')}</span>
          </div>
          <details className="wb-simple-actions" onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); } }}>
            <summary aria-label={t('workoutDetail.actions.more')}><span aria-hidden>•••</span></summary>
            <div className="wb-simple-actions__menu" onClick={e => { const button = (e.target as HTMLElement).closest('button'); if (button && !button.disabled) { const menu = e.currentTarget.closest('details'); if (menu) menu.open = false; } }}>
            <ActionIconButton
              type="button"
              className="wb-mobile-repeat__btn wb-mobile-repeat__btn--edit"
              onClick={() => onLoopPress(String(blockIdx))}
              title={t('workoutBuilder.editRepeat')}
              label={t('workoutBuilder.editRepeat')}
              icon={<IoOptionsOutline aria-hidden />}
            />
            {!dragActive && (
              <>
                <ActionIconButton
                  type="button"
                  className="wb-mobile-repeat__drag-hint"
                  title={t('workoutBuilder.reorderHint')}
                  onClick={e => e.stopPropagation()}
                  onPointerDown={e => onSlotPointerDown(scope, blockIdx, e)}
                  label={t('workoutBuilder.reorderHint')}
                  icon={<IoReorderThreeOutline aria-hidden />}
                />
                {renderMoveButtons(scope, blockIdx, data.length, 'repeat')}
                <ActionIconButton
                  type="button"
                  className="wb-mobile-repeat__btn wb-mobile-repeat__btn--dup"
                  onClick={() => Duplicate(blockIdx)}
                  title={t('workoutBuilder.duplicateSection')}
                  label={t('workoutBuilder.duplicateSection')}
                  icon={<IoCopyOutline aria-hidden />}
                />
                <ActionIconButton
                  type="button"
                  className="wb-mobile-repeat__btn wb-mobile-repeat__btn--del"
                  onClick={() => { rememberBeforeDelete(); Delete(null, item, blockIdx); }}
                  variant="danger"
                  label={t('workoutBuilder.deleteStep')}
                  icon={<IoTrashOutline aria-hidden />}
                />
              </>
            )}
          </div>
          </details>
        </div>
        <div className="wb-mobile-repeat__steps">
          {innerSteps.map((innerItem, innerIdx) => {
            const iStep = getInnerStep(innerItem as ApiStructBlock);
            return renderStepCard(
              iStep,
              innerIdx,
              blockIdx,
              () => Delete(blockIdx, item, innerIdx),
              true,
              undefined,
              innerSteps.length,
            );
          })}
        </div>
      </div>
    );
  };

  const paletteLabel = useCallback(
    (def: PaletteBlockDef) => t(`workoutBuilder.palette.${def.id}`, def.title),
    [t],
  );

  return (
    <div className="wb-builder wb-builder--simple">
      {settingsConfirmed && settingsToolbar && createPortal(
        <ActionButton
          type="button"
          className="wb-mobile-chart-card__settings"
          onClick={openSettings}
          wrap
        ><IoOptionsOutline size={20} aria-hidden /><span>{t('workoutBuilder.settingsAction')}</span></ActionButton>, settingsToolbar,
      )}
      {hasBlocks && <div className="wb-mobile-chart-card">
        <div className="wb-mobile-chart-card__header">
          <span className="wb-mobile-chart-card__title">{t('workoutBuilder.powerChart')}</span>
          {settingsConfirmed && !settingsToolbar && <ActionButton
            type="button"
            className="wb-mobile-chart-card__settings"
            onClick={openSettings}
            wrap
          ><IoOptionsOutline size={20} aria-hidden /><span>{t('workoutBuilder.settingsAction')}</span></ActionButton>}
        </div>
        <WorkoutStructureBar structure={data} containerWidthPercent={92} maxHeightPx={72} barHeightPx={48} layoutMode="duration" primaryIntensityMetric={settings.primaryIntensityMetric} ftpValue={ftpValue} ltHrValue={ltHrValue} />
      </div>}
      {!hasBlocks && settingsConfirmed && !settingsToolbar && <ActionButton
        type="button"
        className="wb-mobile-chart-card__settings"
        onClick={openSettings}
        wrap
      ><IoOptionsOutline size={20} aria-hidden /><span>{t('workoutBuilder.settingsAction')}</span></ActionButton>}

      {hasBlocks && (
        <div className="wb-mobile-totals">
          <div className="wb-mobile-totals__stat">
            <span className="wb-mobile-totals__val" style={{ color: 'var(--color-accent)' }}>{totals.durStr}</span>
            <span className="wb-mobile-totals__lbl">{t('workoutBuilder.totalDuration')}</span>
          </div>
          <div className="wb-mobile-totals__divider" />
          <div className="wb-mobile-totals__stat">
            <span className="wb-mobile-totals__val" style={{ color: 'var(--text-success)' }}>{totals.stepCount}</span>
            <span className="wb-mobile-totals__lbl">{t('workoutBuilder.totalSteps')}</span>
          </div>
          <div className="wb-mobile-totals__divider" />
          <div className="wb-mobile-totals__stat">
            <span className="wb-mobile-totals__val" style={{ color: 'var(--text-warning)' }}>{displayDigits(totals.tss ?? '—')}</span>
            <span className="wb-mobile-totals__lbl">{t('workoutBuilder.trainingPressure')}</span>
          </div>
        </div>
      )}

      <div className={`wb-mobile-step-list${isReordering ? ' wb-mobile-step-list--reordering' : ''}`}>
        {(data ?? []).map((item, idx) => {
          if (item?.type === 'step') {
            const step = getInnerStep(item);
            if (!step) return null;
            return renderStepCard(
              step,
              idx,
              null,
              () => Delete(null, item, idx),
              false,
              () => Duplicate(idx),
              data.length,
            );
          }
          if (item?.type === 'repetition') {
            return renderRepeatBlock(item, idx);
          }
          return null;
        })}
      </div>

      {undoSnapshot && (
        <div className="wb-builder-undo" role="status" aria-live="polite">
          <span>{t('workoutBuilder.stepDeleted')}</span>
          <ActionButton
            type="button"
            onClick={undoDelete}
          >{t('workoutBuilder.undo')}</ActionButton>
        </div>
      )}

      <details className="wb-simple-add">
        <summary><IoAddOutline aria-hidden />{t('workoutBuilder.addStep')}</summary>
      <div className="wb-mobile-palette-wrap">
        <div className="wb-mobile-palette-scroll">
          {PALETTE_BLOCKS.map(def => {
            const { bars, repeatCount } = buildPillBars(def);
            return (
              <button
                key={def.id}
                type="button"
                className={`wb-mobile-palette-pill${def.wide ? ' wb-mobile-palette-pill--wide' : ''}`}
                onClick={() => appendBlocks(def.blocks as ApiStructBlock[])}
                aria-label={t('workoutBuilder.addBlock', { name: paletteLabel(def) })}>
                <span className="wb-mobile-palette-pill__top">
                  <span className="wb-mobile-palette-pill__label">{paletteLabel(def)}</span>
                  <span className="wb-mobile-palette-pill__add" aria-hidden><IoAddOutline /></span>
                </span>
                <div className="wb-mobile-palette-pill__bars" dir="ltr">
                  {bars.map((b, i) => (
                    <div
                      key={i}
                      className="wb-mobile-palette-pill__bar"
                      style={{
                        height: Math.max(3, (b.val / 100) * PILL_BAR_MAX_H),
                        backgroundColor: b.color,
                      }}
                    />
                  ))}
                  {repeatCount != null && (
                    <span className="wb-mobile-palette-pill__repeat">{repeatCount}×</span>
                  )}
                </div>
              </button>
            );
          })}
          <div style={{ width: 24, flexShrink: 0 }} />
        </div>
        <div className="wb-mobile-palette-fade" />
      </div>

      </details>

      <WorkoutBuilderSetupModal
        open={setupModalOpen}
        settings={draftSettings}
        onChange={setDraftSettings}
        onConfirm={confirmSettings}
        onCancel={settingsConfirmed || onSetupCancel ? handleSetupCancel : undefined}
        confirmed={settingsConfirmed}
      />
    </div>
  );
}
